import { duration } from 'dayjs';
import { Howl, Howler, HowlOptions } from 'howler';
import { useEffect, useRef } from 'react';
import { REHYDRATE } from 'redux-persist';
import { gameConfigStore } from './store/GameConfigStore';
import { startAppListening } from './store/Store';
import { Log } from './util/Logging';

export const Sound = {
	initialize,
	music: {
		ThemeSong: new Howl(create('GameShowAirstream.mp3')),
		Winner: new Howl(create('BingoCrazy.mp3')),
	},
	effects: {
		BoardCleared: new Howl(create('BoardCleared.mp3')),
		Buzzer: new Howl(create('Buzzer.mp3')),
		CorrectAnswer: new Howl(create('CorrectAnswer.wav')),
		StrikeOut: new Howl(create('FailSting.mp3')),
		WrongAnswer: new Howl(create('WrongAnswer.wav')),
		RoundWin: new Howl(create('RoundWin.wav')),
	}
};

function initialize() {
	startAppListening({
		type: REHYDRATE,
		effect: (_action, api) => {
			const sensoryMode = gameConfigStore.selectors.sensorySensitivityMode(api.getState());
			Log.audio.debug('Setting audio mute to', sensoryMode);
			Howler.mute(sensoryMode);
		},
	});

	startAppListening({
		actionCreator: gameConfigStore.actions.setSensorySensitivityMode,
		effect: (action) => {
			Log.audio.debug('Setting audio mute to', action.payload);
			Howler.mute(action.payload);
		},
	});
}

function canPlay(): boolean {
	return Howler.ctx.state === 'running';
}

function create(name: string): HowlOptions {
	return {
		src: `/audio/${name}`,
		onload: (soundId) => Log.audio.debug(`Track ${name} ${soundId} loaded`),
		onloaderror: (soundId, error) => Log.audio.warn(`Track ${name} ${soundId} failed to load`, error),
		onplay: (soundId) => Log.audio.debug(`Playing ${name} ${soundId}`),
		onplayerror: (soundId, error) => Log.audio.warn(`Track ${name} ${soundId} failed to play`, error),
	};
};

export function useGameSound(sound: Howl, enabled: boolean) {
	const previousEnabled = useRef(false);

	useEffect(() => {
		if (enabled && !previousEnabled.current && canPlay()) {
			sound.play();
		}
		previousEnabled.current = enabled;
	}, [enabled, sound]);
}

const FADE_DURATION = duration(5, 'seconds');

export function useMusic(track: Howl, enabled = true) {
	const previousEnabled = useRef(false);

	useEffect(() => {
		// Don't attempt to play while suspended as sound will be queued and play on first user interaction
		if (enabled && !previousEnabled.current) {
			if (canPlay()) {
				track.volume(1);
				track.seek(0);
				track.play();
			} else {
				Log.audio.warn('Unable to play music before first user interaction');
			}
		} else if (!enabled && track.playing()) {
			Log.audio.debug('Stopping');
			track.fade(track.volume(), 0, FADE_DURATION.asMilliseconds());
		}

		return () => {
			if (track.playing()) {
				Log.audio.debug('Stopping');
				track.fade(track.volume(), 0, FADE_DURATION.asMilliseconds());
			}
		};
	}, [enabled]);
};
